/**
 * /enfants — Étiquettes école 50×30 mm (même format que réparations).
 * Privé : Carlos uniquement. Texte + images PNG déplaçables.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, Eye, ImagePlus, Plus, Printer, Save, Trash2, Type, Move,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const LABEL_W_MM = 50;
const LABEL_H_MM = 30;
const PREVIEW_SCALE = 8; // px per mm → 400×240

const uid = () => `L${Math.random().toString(36).slice(2, 9)}`;

const defaultLayers = () => ([
  {
    id: uid(), type: 'text', text: 'École',
    x: 4, y: 6, w: 92, h: 16, fontSize: 14, bold: true, align: 'center', color: '#111111', z: 1,
  },
  {
    id: uid(), type: 'text', text: 'Prénom NOM',
    x: 4, y: 28, w: 92, h: 28, fontSize: 18, bold: true, align: 'center', color: '#000000', z: 2,
  },
  {
    id: uid(), type: 'text', text: 'Classe / Année',
    x: 4, y: 62, w: 92, h: 16, fontSize: 12, bold: false, align: 'center', color: '#333333', z: 3,
  },
  {
    id: uid(), type: 'text', text: 'Tél. parent',
    x: 4, y: 82, w: 92, h: 12, fontSize: 9, bold: false, align: 'center', color: '#555555', z: 4,
  },
]);

function pctToPx(pct, total) {
  return (Number(pct) / 100) * total;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function EnfantsLabelsPage() {
  const { api } = useAuth();
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const [layers, setLayers] = useState(defaultLayers);
  const [selectedId, setSelectedId] = useState(null);
  const [background, setBackground] = useState('#FFFFFF');
  const [copies, setCopies] = useState(12);
  const [templates, setTemplates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetUrls, setAssetUrls] = useState({});
  const [templateName, setTemplateName] = useState('Étiquette école');
  const [busy, setBusy] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [msg, setMsg] = useState('');
  const [livePreview, setLivePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const canvasW = LABEL_W_MM * PREVIEW_SCALE;
  const canvasH = LABEL_H_MM * PREVIEW_SCALE;
  const selected = layers.find((l) => l.id === selectedId) || null;

  const loadBootstrap = useCallback(async () => {
    try {
      const res = await api.get('/enfants/bootstrap');
      setTemplates(res.data.templates || []);
      setAssets(res.data.assets || []);
      if (res.data.default_layers?.length && layers.length === 0) {
        setLayers(res.data.default_layers.map((L) => ({ ...L, id: L.id || uid() })));
      }
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Accès refusé ou API indisponible');
    }
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = { ...assetUrls };
      for (const a of assets) {
        if (next[a.id]) continue;
        try {
          const res = await api.get(`/enfants/assets/${a.id}`, { responseType: 'blob' });
          next[a.id] = URL.createObjectURL(res.data);
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setAssetUrls(next);
    };
    load();
    return () => { cancelled = true; };
  }, [assets, api]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLayer = (id, patch) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addText = () => {
    const id = uid();
    setLayers((prev) => [...prev, {
      id, type: 'text', text: 'Nouveau texte',
      x: 10, y: 40, w: 80, h: 20, fontSize: 14, bold: false, align: 'center', color: '#000000', z: prev.length + 1,
    }]);
    setSelectedId(id);
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/enfants/assets', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const asset = res.data;
      setAssets((prev) => [asset, ...prev]);
      const id = uid();
      setLayers((prev) => [...prev, {
        id, type: 'image', asset_id: asset.id,
        x: 5, y: 5, w: 30, h: 40, opacity: 1, z: prev.length + 1,
      }]);
      setSelectedId(id);
      setMsg('Image ajoutée — déplacez-la sur l\'étiquette');
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Upload impossible');
    } finally {
      setBusy(false);
    }
  };

  const removeSelected = () => {
    if (!selectedId) return;
    setLayers((prev) => prev.filter((l) => l.id !== selectedId));
    setSelectedId(null);
  };

  const onPointerDown = (e, layer) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(layer.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      id: layer.id,
      mode: e.target.dataset.handle === 'resize' ? 'resize' : 'move',
      startX: e.clientX,
      startY: e.clientY,
      ox: layer.x,
      oy: layer.y,
      ow: layer.w,
      oh: layer.h,
      rectW: rect.width,
      rectH: rect.height,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ((e.clientX - d.startX) / d.rectW) * 100;
      const dy = ((e.clientY - d.startY) / d.rectH) * 100;
      if (d.mode === 'move') {
        updateLayer(d.id, {
          x: clamp(d.ox + dx, 0, 95),
          y: clamp(d.oy + dy, 0, 95),
        });
      } else {
        updateLayer(d.id, {
          w: clamp(d.ow + dx, 5, 100 - d.ox),
          h: clamp(d.oh + dy, 5, 100 - d.oy),
        });
      }
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const renderBody = useMemo(() => ({
    layers,
    background,
    copies,
  }), [layers, background, copies]);

  // Aperçu exact (rendu serveur) — se met à jour au fur et à mesure
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post('/enfants/preview.png', {
          layers,
          background,
          copies: 1,
        }, { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setLivePreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch {
        /* ignore preview errors while typing */
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [layers, background, api]);

  const printBixolon = async () => {
    setQueueing(true);
    setMsg('');
    try {
      const res = await api.post('/enfants/print', renderBody);
      const id = res.data?.job_id;
      setMsg(
        `${res.data?.labels_count || copies} etiquette(s) envoyee(s) a l'agent Bixolon`
        + (id ? ` (${String(id).slice(0, 8)}…)` : '')
        + ' — meme canal que les etiquettes reparation.',
      );
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message || 'Erreur envoi Bixolon');
    } finally {
      setQueueing(false);
    }
  };

  const openPdf = async () => {
    setBusy(true);
    setMsg('');
    try {
      const res = await api.post('/enfants/render.pdf', renderBody, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setMsg('PDF ouvert (secours) — preferer Imprimer Bixolon.');
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message || 'Erreur PDF');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.post('/enfants/render.pdf', renderBody, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiquettes-ecole-${copies}x-50x30.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Telechargement impossible');
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = async () => {
    setBusy(true);
    try {
      const res = await api.post('/enfants/templates', {
        name: templateName || 'Etiquette ecole',
        layers,
        background,
        copies_default: copies,
      });
      setTemplates((prev) => [res.data, ...prev]);
      setMsg('Modele enregistre');
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  };

  const loadTemplate = (t) => {
    setLayers((t.layers || []).map((L) => ({ ...L, id: L.id || uid() })));
    setBackground(t.background || '#FFFFFF');
    setCopies(t.copies_default || 1);
    setTemplateName(t.name || 'Étiquette');
    setSelectedId(null);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-3 py-4 space-y-4" data-testid="enfants-labels-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-white">Étiquettes école</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              Format <strong className="text-gray-200">50 × 30 mm</strong> (identique aux étiquettes réparation) · espace privé Carlos · URL /enfants
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={addText} className="min-h-[40px] px-3 rounded-lg bg-white/10 text-sm flex items-center gap-1.5">
              <Type className="w-4 h-4" /> Texte
            </button>
            <label className="min-h-[40px] px-3 rounded-lg bg-white/10 text-sm flex items-center gap-1.5 cursor-pointer">
              <ImagePlus className="w-4 h-4" /> Image PNG
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUploadImage} />
            </label>
            <button type="button" onClick={saveTemplate} disabled={busy} className="min-h-[40px] px-3 rounded-lg bg-emerald-900/50 text-emerald-200 text-sm flex items-center gap-1.5">
              <Save className="w-4 h-4" /> Sauver modele
            </button>
            <button
              type="button"
              onClick={printBixolon}
              disabled={busy || queueing}
              className="min-h-[40px] px-3 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-bold flex items-center gap-1.5"
              data-testid="enfants-print-bixolon"
            >
              <Printer className="w-4 h-4" />
              {queueing ? 'Envoi…' : `Imprimer Bixolon (${copies})`}
            </button>
            <button type="button" onClick={openPdf} disabled={busy} className="min-h-[40px] px-3 rounded-lg bg-white/10 text-sm flex items-center gap-1.5" data-testid="enfants-print-pdf">
              <Eye className="w-4 h-4" /> PDF (secours)
            </button>
            <button type="button" onClick={downloadPdf} disabled={busy} className="min-h-[40px] px-3 rounded-lg bg-white/10 text-sm flex items-center gap-1.5">
              <Download className="w-4 h-4" /> Telecharger
            </button>
          </div>
        </div>

        {msg && <p className="text-sm text-amber-200 bg-amber-950/40 border border-amber-500/30 rounded-lg px-3 py-2">{msg}</p>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <div className="rounded-xl border border-white/10 bg-[#151820] p-4">
              <div className="flex items-center justify-between mb-3 text-xs text-gray-400">
                <span className="flex items-center gap-1"><Move className="w-3.5 h-3.5" /> Glissez pour déplacer · coin bas-droit pour redimensionner</span>
                <span>{LABEL_W_MM} × {LABEL_H_MM} mm</span>
              </div>
              <div className="flex justify-center">
                <div
                  ref={canvasRef}
                  className="relative border border-dashed border-white/30 shadow-inner overflow-hidden select-none"
                  style={{ width: canvasW, height: canvasH, background }}
                  onClick={() => setSelectedId(null)}
                  data-testid="enfants-canvas"
                >
                  {[...layers].sort((a, b) => (a.z || 0) - (b.z || 0)).map((layer) => {
                    const left = pctToPx(layer.x, canvasW);
                    const top = pctToPx(layer.y, canvasH);
                    const width = pctToPx(layer.w, canvasW);
                    const height = pctToPx(layer.h, canvasH);
                    const active = layer.id === selectedId;
                    return (
                      <div
                        key={layer.id}
                        onPointerDown={(e) => onPointerDown(e, layer)}
                        className={`absolute overflow-hidden ${active ? 'ring-2 ring-cyan-400' : 'ring-1 ring-black/10'}`}
                        style={{
                          left, top, width, height,
                          cursor: 'move',
                          opacity: layer.opacity ?? 1,
                        }}
                      >
                        {layer.type === 'image' && assetUrls[layer.asset_id] ? (
                          <img src={assetUrls[layer.asset_id]} alt="" className="w-full h-full object-contain pointer-events-none" draggable={false} />
                        ) : (
                          <div
                            className="w-full h-full flex items-center pointer-events-none px-0.5"
                            style={{
                              justifyContent: layer.align === 'center' ? 'center' : layer.align === 'right' ? 'flex-end' : 'flex-start',
                              color: layer.color || '#000',
                              fontWeight: layer.bold ? 700 : 400,
                              fontSize: Math.max(8, (layer.fontSize || 12) * PREVIEW_SCALE * 0.28),
                              lineHeight: 1.1,
                              textAlign: layer.align || 'left',
                            }}
                          >
                            {layer.text || '…'}
                          </div>
                        )}
                        {active && (
                          <div
                            data-handle="resize"
                            className="absolute right-0 bottom-0 w-3 h-3 bg-cyan-400 cursor-se-resize"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#151820] p-3 flex flex-wrap gap-3 items-center">
              <label className="text-xs text-gray-400">
                Fond
                <input type="color" value={background} onChange={(e) => setBackground(e.target.value)} className="ml-2 align-middle" />
              </label>
              <label className="text-xs text-gray-400">
                Copies
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
                  className="ml-2 w-16 rounded bg-black/40 border border-white/10 px-2 py-1 text-white"
                />
              </label>
              <input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="flex-1 min-w-[140px] rounded bg-black/40 border border-white/10 px-2 py-1.5 text-sm text-white"
                placeholder="Nom du modele"
              />
            </div>

            <div className="rounded-xl border border-cyan-500/30 bg-[#0f141c] p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-cyan-200 flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5" /> Apercu impression (live)
                </p>
                {previewLoading && <span className="text-[10px] text-gray-500">maj…</span>}
              </div>
              {livePreview ? (
                <img
                  src={livePreview}
                  alt="apercu etiquette"
                  className="mx-auto border border-white/20 bg-white"
                  style={{ width: canvasW, height: canvasH, imageRendering: 'pixelated' }}
                  data-testid="enfants-live-preview"
                />
              ) : (
                <div className="mx-auto flex items-center justify-center text-xs text-gray-500 border border-dashed border-white/20" style={{ width: canvasW, height: canvasH }}>
                  Apercu en cours…
                </div>
              )}
              <p className="text-[10px] text-gray-500 mt-2 text-center">
                Se met a jour quand vous tapez ou deplacez — meme image envoyee au Bixolon.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl border border-white/10 bg-[#151820] p-3">
              <h3 className="text-sm font-bold text-white mb-2">Élément sélectionné</h3>
              {!selected ? (
                <p className="text-xs text-gray-500">Cliquez un texte ou une image sur l&apos;étiquette.</p>
              ) : (
                <div className="space-y-2 text-xs">
                  {selected.type === 'text' ? (
                    <>
                      <label className="block text-gray-400">
                        Texte
                        <textarea
                          value={selected.text || ''}
                          onChange={(e) => updateLayer(selected.id, { text: e.target.value })}
                          rows={3}
                          className="mt-1 w-full rounded bg-black/40 border border-white/10 px-2 py-1.5 text-white"
                        />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-gray-400">
                          Taille %
                          <input type="number" value={selected.fontSize || 12} onChange={(e) => updateLayer(selected.id, { fontSize: Number(e.target.value) })} className="mt-1 w-full rounded bg-black/40 border border-white/10 px-2 py-1 text-white" />
                        </label>
                        <label className="text-gray-400">
                          Couleur
                          <input type="color" value={selected.color || '#000000'} onChange={(e) => updateLayer(selected.id, { color: e.target.value })} className="mt-1 w-full h-8" />
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => updateLayer(selected.id, { bold: !selected.bold })} className={`px-2 py-1 rounded ${selected.bold ? 'bg-white text-black' : 'bg-white/10'}`}>Gras</button>
                        {['left', 'center', 'right'].map((a) => (
                          <button key={a} type="button" onClick={() => updateLayer(selected.id, { align: a })} className={`px-2 py-1 rounded ${selected.align === a ? 'bg-cyan-700' : 'bg-white/10'}`}>{a}</button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400">Image — déplacez / redimensionnez sur le canevas.</p>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {['x', 'y', 'w', 'h'].map((k) => (
                      <label key={k} className="text-gray-400 uppercase">
                        {k} %
                        <input
                          type="number"
                          value={Math.round(selected[k] * 10) / 10}
                          onChange={(e) => updateLayer(selected.id, { [k]: Number(e.target.value) })}
                          className="mt-1 w-full rounded bg-black/40 border border-white/10 px-2 py-1 text-white"
                        />
                      </label>
                    ))}
                  </div>
                  <button type="button" onClick={removeSelected} className="w-full mt-2 min-h-[36px] rounded-lg bg-red-900/40 text-red-200 flex items-center justify-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-[#151820] p-3">
              <h3 className="text-sm font-bold text-white mb-2">Modèles sauvés</h3>
              {templates.length === 0 ? (
                <p className="text-xs text-gray-500">Aucun modèle pour l&apos;instant.</p>
              ) : (
                <ul className="space-y-1 max-h-48 overflow-y-auto">
                  {templates.map((t) => (
                    <li key={t.id}>
                      <button type="button" onClick={() => loadTemplate(t)} className="w-full text-left text-xs px-2 py-2 rounded hover:bg-white/10 text-gray-200">
                        {t.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-[#151820] p-3">
              <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Astuce</h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Modifiez le texte a gauche : l&apos;apercu live se met a jour tout de suite.
                Puis <strong className="text-gray-300">Imprimer Bixolon</strong> — meme agent que les etiquettes reparation (ZPL 50×30, pas de PDF).
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
